import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const quoteProofPanelSkill: CodeoSkill = {
    name: SKILL_NAMES.quoteProofPanel,
    kind: 'codeo',
    description:
        'Land a short quotation with its attribution arriving after it — for a testimonial, a ' +
        'review, a customer line, or any claim that carries weight because someone said it.',
    documentUsesSkill: check(
        (script, markup) =>
            /&ldquo;|&rdquo;|&quot;|[“”„]|<blockquote/.test(markup) && /scaleX/.test(script),
    ),
    instructions: `# Quote Proof Panel

A short quotation arrives, holds, and then the attribution lands under it. The order is the technique: the words first, so they are read as a statement, and the source second, so it settles who said it. Reversed, the viewer reads the name and then evaluates the quote, which is much weaker.

The quotation has to be genuinely short. A testimonial paragraph on screen is unreadable at video pace, and the fix is always to cut it rather than to shrink the type.

## Markup

The mark is decoration, so keep it well behind the words in visual weight. Anything more than a tint and it competes with the quotation.

\`\`\`html
<div id="quote-panel" style="position: absolute; left: 280px; top: 300px; width: 1360px;">
  <div id="quote-mark" style="position: absolute; left: -40px; top: -80px; font-family: var(--l5-font-0), serif; font-size: 320px; line-height: 1; color: var(--l5-color-2, #38bdf8); opacity: 0.16;">&ldquo;</div>
  <div id="quote-body" style="position: relative; font-family: var(--l5-font-0), sans-serif; font-size: 82px; font-weight: 600; line-height: 1.22; color: var(--l5-color-0, #101820);">We stopped arguing about the deck and started shipping.</div>
  <div id="quote-rule" style="margin-top: 44px; width: 120px; height: 5px; background: var(--l5-color-2, #38bdf8); transform-origin: left center;"></div>
  <div id="quote-source" style="margin-top: 26px; font-family: var(--l5-font-0), sans-serif; font-size: 36px; font-weight: 600; color: var(--l5-color-1, #5b6472);">Priya Raman &middot; Head of Brand, Northwind</div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ATTRIBUTE = 2.3;

gsap.set('#quote-mark', { scale: 0.8, opacity: 0, transformOrigin: 'left top' });
gsap.set('#quote-body', { y: 22, opacity: 0 });
gsap.set('#quote-rule', { scaleX: 0 });
gsap.set('#quote-source', { y: 14, opacity: 0 });

tl.to('#quote-mark', { scale: 1, opacity: 0.16, duration: 0.7, ease: 'power3.out' }, 0.2);
tl.to('#quote-body', { y: 0, opacity: 1, duration: 0.75, ease: 'power3.out' }, 0.35);

// The source arrives only once the quotation has been read.
tl.to('#quote-rule', { scaleX: 1, duration: 0.4, ease: 'power3.out' }, ATTRIBUTE);
tl.to('#quote-source', { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, ATTRIBUTE + 0.16);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Attribution delay**: about 0.35s of hold per word of the quotation. Rushing the source is the most common way this fails.
- **Per-line reveal**: for a two-line quotation, stagger the lines 0.18s apart — see \`${SKILL_NAMES.kineticWordReveal}\` for the word-level version.
- **Emphasis inside the quote**: marking the phrase that matters once the line has settled makes a long quotation survivable — see \`${SKILL_NAMES.markerEmphasis}\`.
- **Avatar**: a circular portrait beside the source, arriving with it. It must not arrive with the quotation, or the face is read before the words.
- **Rating above**: a star row landing before the quote frames it as a review rather than a statement — see \`${SKILL_NAMES.ratingAndProgressFill}\`.
- **Several quotes**: use \`${SKILL_NAMES.sharedAxisSlide}\` between them, and give each its full attribution beat. Two is realistically the maximum in one scene.

## Pitfalls

- The rule is \`scaleX\`-tweened from a left origin and must not carry a CSS \`transform\`; the mark is \`scale\`-tweened and needs its origin set through \`gsap.set\`.
- Keep the quotation under about 14 words. Longer and the viewer is still reading when the scene ends.
- The quotation mark must stay decorative. At full opacity it reads as a design element competing with the words.
- Don't restate the quotation in the narration. If the voiceover reads it aloud, the graphic should carry the source and the emphasis, not a duplicate of the sentence.
- The source line is one line. A role, a company and a location wrapping to two lines turns the attribution into a block.
- Attributing a quotation to a real, named person makes a factual claim — only use real names the video is entitled to use.
- After the source lands the frame is completely still; give the panel a drift — see \`${SKILL_NAMES.driftAndHold}\`.`,
};
